import { EmailSendModal } from "@/components/email/EmailSendModal.tsx";
import { NotificationCount } from "@/components/notification-count/NotificationCount.tsx";
import { Search } from "@/components/search/Search.tsx";
import { parseUrlAppHelper } from "@/helpers/parse-url.helper.ts";
import {
  parseUrlHelper,
  Avatar,
  Box,
  Button,
  ButtonIcon,
  DropdownMenu,
  DropdownMenuItem,
  EmptyStateBase,
  FontAwesomeIcon,
  Stack,
  Text,
  useModals,
} from "@skailer-com/uikit";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { ApplicantDetails } from "@/pages/recruit/candidates/components/ApplicantDetails.tsx";
import { ApplicantAssignToJobModal } from "@/pages/recruit/candidates/widgets/ApplicantAssignToJobModal.tsx";
import { ApplicantEditModal } from "@/pages/recruit/candidates/widgets/ApplicantEditModal.tsx";
import { ApplicantsFilterModal } from "@/pages/recruit/candidates/widgets/ApplicantsFilterModal.tsx";
import { ChangeStageModal } from "@/pages/recruit/candidates/widgets/ChangeStageModal.tsx";
import { ActionItem } from "@/pages/recruit/components/ActionItem.tsx";
import { ApplicantListItem } from "@/pages/recruit/components/ApplicantListItem.tsx";
import { ApplicantListItemSkeleton } from "@/pages/recruit/components/ApplicantListItemSkeleton.tsx";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import { useApplicantsSearchInfinite } from "@/queries/recruit/use-applicants.query.ts";
import { useCandidateCreate } from "@/queries/recruit/use-candidates.query.ts";
import { useJobDetails } from "@/queries/recruit/use-jobs.query.ts";
import { useEntityFormConfig } from "@/queries/use-custom-fields.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { useEmployeeAppConfigStore } from "@/stores/employee.store.ts";
import { ApiAIProcessingStatus } from "@/types/ai/ai.types.ts";
import { ApiSorting } from "@/types/api.types.ts";
import { ApiEntityTypes } from "@/types/common.types.ts";
import { ApiCFEntityTypes, ApiFieldTypes } from "@/types/custom-field.types.ts";
import {
  ApiApplicantFilter,
  ApiApplicantMentionState,
  ApiApplicantShort,
  ApplicantFilterDefault,
  ApplicantShortEmpty,
  ApplicantSortField,
  getCandidateScoreMarkAvatarState,
} from "@/types/recruit/applicant.types.ts";
import { ApiCandidateViaApplicantFilter } from "@/types/recruit/candidate.types.ts";
import { getJobEmoji } from "@/types/recruit/job.types.ts";
import { PipelineStageTypes } from "@/types/recruit/stages.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import { Allotment } from "allotment";
import { isEmpty, omitBy } from "lodash";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDebounce } from "react-use";
import { twMerge } from "tailwind-merge";

const parseMentionState = (searchParams: URLSearchParams) => {
  const mentionStateString = searchParams.get("mention_state") ?? undefined;
  return Object.values(ApiApplicantMentionState).includes(
    mentionStateString as ApiApplicantMentionState
  )
    ? (mentionStateString as ApiApplicantMentionState)
    : undefined;
};

export default function Applicants() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isFeatureEnabled } = usePermissions();
  const { id = undefined } = useParams();
  const currentApplicantId = useMemo(() => (id ? parseInt(id, 10) : undefined), [id]);

  const { sidebarIsCollapsed } = useEmployeeAppConfigStore();

  const candidateCreate = useCandidateCreate();
  const modalsService = useModals();

  const { data: employeeMe } = useEmployeeMe();

  const [filter, setFilter] = useState<ApiApplicantFilter>(ApplicantFilterDefault);
  const {
    data: applicants,
    isLoading,
    isFetching,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    refetch,
  } = useApplicantsSearchInfinite(filter);
  const isDataLoading = isLoading || isFetching || isFetchingNextPage;
  const totalApplicants =
    applicants && applicants.pages.length > 0 && applicants.pages[0]
      ? applicants.pages[0].metadata.total_items
      : 0;
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: jobDetails } = useJobDetails(
    filter.job_ids && filter.job_ids.length == 1 ? filter.job_ids[0] : undefined
  );

  const [multiSelectionModeOn, setMultiSelectionModeOn] = useState<boolean>(false);
  const [selectedApplicants, setSelectedApplicants] = useState<ApiApplicantShort[]>([]);
  const [isSelectAll, setIsSelectAll] = useState<boolean>(false);
  const { recruitLeftPaneSize, setRecruitLeftPaneSize } = useEmployeeAppConfigStore();

  const { canIEdit } = usePermissions();
  const { hasField } = useEntityFormConfig(ApiCFEntityTypes.applicant);

  const [isUserTyping, setIsUserTyping] = useState(false);
  const [searchTerm, setSearchTerm] = useState(filter.text);

  useDebounce(
    () => {
      if (!isUserTyping) return;

      const newParams: Record<string, string | string[]> = omitBy(
        {
          ...Object.fromEntries(searchParams),
          text: searchTerm,
        },
        isEmpty
      ) as Record<string, string | string[]>;
      setSearchParams(newParams);

      setIsUserTyping(false);
    },
    300,
    [searchTerm]
  );

  useEffect(() => {
    const messageListener = (event: MessageEvent) => {
      if (event.source !== window) return;
      if (event.data.type !== "FROM_EXTENSION") return;
      if (!event.data.payload) return;
      if (!event.data.payload.action) return;

      const action: string = event.data.payload.action;
      const actionForApplicants =
        [
          "APPLICANT_CREATED",
          "APPLICANT_UPDATED",
          "CANDIDATE_CREATED",
          "COMMENT_ADDED",
          "LABEL_UPDATED",
          "SOURCE_UPDATED",
        ].indexOf(action) !== -1;

      if (actionForApplicants) refetch();
    };

    window.addEventListener("message", messageListener);

    return () => {
      window.removeEventListener("message", messageListener);
    };
  }, []);

  const handleDismissSelectable = useCallback(() => {
    setSelectedApplicants([]);
    setIsSelectAll(false);
    toast.remove("toastBar");
  }, []);

  const [syncedSearchParams, setSyncedSearchParams] = useState<URLSearchParams>();
  if (searchParams !== syncedSearchParams) {
    setSyncedSearchParams(searchParams);
    const stageIdsParams: number[] = parseUrlHelper.getNumberArray(searchParams, "stage_id");
    const jobIds = parseUrlHelper.getNumberArray(searchParams, "job_id");
    const text = searchParams.get("text") || undefined;
    const location = searchParams.get("location") || undefined;
    const company = searchParams.get("company") || undefined;
    const sourceIds = parseUrlHelper.getNumberArray(searchParams, "source_id");
    const disqualificationReasonIds = parseUrlHelper.getNumberArray(
      searchParams,
      "disqualification_reason_id"
    );
    const position = searchParams.get("position") || undefined;
    const disqualified = searchParams.get("disqualified")
      ? Boolean(searchParams.get("disqualified") == "true")
      : undefined;
    const reserved = searchParams.get("reserved")
      ? Boolean(searchParams.get("reserved") == "true")
      : disqualified
        ? false
        : undefined;
    const dateLastCandidateActionFrom = parseUrlAppHelper.getDate(
      searchParams,
      "last_candidate_action_from"
    );
    const dateLastCandidateActionTo = parseUrlAppHelper.getDate(
      searchParams,
      "last_candidate_action_to"
    );
    const labelIds = parseUrlHelper.getNumberArray(searchParams, "label_id");
    const comment = searchParams.get("comment") || undefined;
    const recruiterIds = parseUrlHelper.getNumberArray(searchParams, "recruiter_id");
    const applicantBirthDateFrom = parseUrlAppHelper.getDate(
      searchParams,
      "date_applicant_birth_from"
    );
    const applicantBirthDateTo = parseUrlAppHelper.getDate(searchParams, "date_applicant_birth_to");
    const mentionState = parseMentionState(searchParams);
    const stakeholderIds = parseUrlHelper.getNumberArray(searchParams, "stakeholder_id");
    const authorIds = parseUrlHelper.getNumberArray(searchParams, "author_id");
    const assignedByIds = parseUrlHelper.getNumberArray(searchParams, "assigned_by_ids");
    const actionEmployeeIds = parseUrlHelper.getNumberArray(searchParams, "action_employee_id");
    const sort = searchParams.get("sort") || undefined;
    const sorting: ApiSorting = {
      ...ApplicantFilterDefault.sorting,
      fields: ApplicantFilterDefault.sorting.fields.map((field) => ({ ...field })),
    };
    if (sort) {
      if (sort == ApplicantSortField.last_action_at)
        sorting.fields[0].field = ApplicantSortField.last_action_at;
      else if (sort == ApplicantSortField.candidate_ai_score) {
        sorting.fields[0].field = ApplicantSortField.candidate_ai_score;
      } else if (sort == ApplicantSortField.candidate_created_at) {
        sorting.fields[0].field = ApplicantSortField.candidate_created_at;
      }
    }

    if (!isUserTyping && text !== searchTerm) {
      setSearchTerm(text);
    }
    setSelectedApplicants([]);
    setIsSelectAll(false);

    setFilter({
      ...filter,
      pipeline_stage_ids: stageIdsParams,
      location: location,
      company: company,
      source_ids: sourceIds,
      disqualification_reason_ids: disqualificationReasonIds,
      position: position,
      job_ids: jobIds ? jobIds : [],
      page_number: 1,
      text: text,
      date_last_candidate_action_from: dateLastCandidateActionFrom,
      date_last_candidate_action_to: dateLastCandidateActionTo,
      reserved,
      disqualified,
      label_ids: labelIds,
      comment: comment,
      recruiter_ids: recruiterIds,
      date_applicant_birth_from: applicantBirthDateFrom,
      date_applicant_birth_to: applicantBirthDateTo,
      mention_state: mentionState,
      stakeholder_ids: stakeholderIds,
      action_employee_ids: actionEmployeeIds,
      author_ids: authorIds,
      assigned_by_ids: assignedByIds,
      sorting: sorting,
    });
  }

  useEffect(() => {
    toast.remove("toastBar");

    if (parseMentionState(searchParams)) {
      analyticsService.trackEvent(analyticEvents.mentions.viewed);
    }
  }, [searchParams]);

  useEffect(() => {
    const cleanup = () => {
      toast.remove("toastBar");
      setSelectedApplicants([]);
      setIsSelectAll(false);
    };

    return () => cleanup();
  }, [location]);

  const handleApplicantClick = useCallback(
    (applicant: ApiApplicantShort) => {
      const mentionStateString = searchParams.get("mention_state");
      const mentionState = Object.values(ApiApplicantMentionState).includes(
        mentionStateString as ApiApplicantMentionState
      );

      const shouldGoToNotes =
        mentionState &&
        applicant.have_unread_mentions &&
        applicant.last_or_unread_comment_data?.entity_type === ApiEntityTypes.applicant;

      if (shouldGoToNotes) {
        navigate(`/recruitment/candidates/${applicant.id}/notes${location.search}`);
      } else {
        navigate(`/recruitment/candidates/${applicant.id}${location.search}`);
      }
      analyticsService.trackEvent(analyticEvents.applicants.viewedApplicant, {
        [analyticProperties.id]: applicant.id,
      });
    },
    [location, searchParams]
  );

  const handleApplicantFilterClick = useCallback(() => {
    modalsService.openModal(ApplicantsFilterModal, { filter });
  }, [filter]);

  const handleNewApplicantClick = useCallback(() => {
    modalsService.openModal(ApplicantEditModal, {
      applicant: ApplicantShortEmpty,
      onSave: (applicantSaved: ApiApplicantShort) => {
        if (jobDetails) {
          candidateCreate.mutate(
            {
              job_id: jobDetails.id,
              applicant_id: applicantSaved.id,
              pipeline_stage_id:
                jobDetails.pipeline.stages.find(
                  (stage) => stage.type === PipelineStageTypes.applied
                )?.id || jobDetails.pipeline.stages[0].id,
              pipeline_id: jobDetails.pipeline.id,
            },
            {
              onSuccess: (candidate) => {
                analyticsService.trackEvent(analyticEvents.applicants.assignedToJob, {
                  [analyticProperties.id]: candidate.id,
                  [analyticProperties.source]: "App",
                });
                navigate(`/recruitment/candidates/${applicantSaved.id}/?job_id=${jobDetails.id}`);
              },
            }
          );
        } else {
          navigate(`/recruitment/candidates/${applicantSaved.id}`);
        }
      },
    });
  }, [filter, jobDetails]);

  const handleSelectApplicant = useCallback(
    (selected: boolean, applicant: ApiApplicantShort) => {
      if (isSelectAll) selected = !selected;
      if (selected) {
        setSelectedApplicants([...selectedApplicants, applicant]);
      } else {
        setSelectedApplicants(
          selectedApplicants.filter((applicantIn) => applicantIn.id !== applicant.id)
        );
      }
    },
    [selectedApplicants]
  );

  const showSelectedCandidates = useCallback(() => {
    toast.custom(
      () => (
        <Stack
          className={"p-2 pl-4 bg-dark/95 rounded-xs text-light"}
          gap={"sm"}
          items={"center"}
          direction={"horizontal"}
        >
          {t("plurals:plural_candidates_selected", {
            count: isSelectAll
              ? totalApplicants - selectedApplicants.length
              : selectedApplicants.length,
          })}
          <Button onClick={handleSelectAll}>{t("common:select_all")}</Button>
          <Button onClick={handleChangeStage}>{t("ats:change_stage")}</Button>
          <Button onClick={handleSendEmail}>{t("core:send_email")}</Button>
          <Button onClick={handleAssignToJob}>{t("ats:assign_to_job")}</Button>
          <ButtonIcon
            onClick={handleDismissSelectable}
            icon={<FontAwesomeIcon icon={"fa-light fa-close"} />}
          />
        </Stack>
      ),
      {
        id: "toastBar",
        duration: Infinity,
        position: "bottom-center",
      }
    );
  }, [selectedApplicants]);

  const [syncedSelectedApplicants, setSyncedSelectedApplicants] = useState(selectedApplicants);
  if (selectedApplicants !== syncedSelectedApplicants) {
    setSyncedSelectedApplicants(selectedApplicants);
    setMultiSelectionModeOn(selectedApplicants.length > 0 || isSelectAll);
  }

  useEffect(() => {
    if (selectedApplicants.length > 0 || isSelectAll) {
      showSelectedCandidates();
    } else {
      toast.remove("toastBar");
    }
  }, [selectedApplicants]);

  const handleSelectAll = useCallback(() => {
    setIsSelectAll(true);
    setSelectedApplicants([]);
  }, [selectedApplicants]);

  const createApplicantFilter = useCallback((): ApiCandidateViaApplicantFilter | undefined => {
    if (!jobDetails) return undefined;

    const applicantFilter = {
      ...filter,
      job_ids: [jobDetails.id],
      pipeline_stage_ids:
        filter.pipeline_stage_ids?.length === 1 ? [filter.pipeline_stage_ids[0]] : undefined,
      analyticsTotalUpdatedCount: 0,
    };

    if (isSelectAll) {
      applicantFilter.exclude_applicant_ids = selectedApplicants.map((applicant) => applicant.id);
      applicantFilter.analyticsTotalUpdatedCount = totalApplicants - selectedApplicants.length;
    } else {
      applicantFilter.applicant_ids = selectedApplicants.map((applicant) => applicant.id);
      applicantFilter.analyticsTotalUpdatedCount = selectedApplicants.length;
    }

    return applicantFilter;
  }, [jobDetails, filter, isSelectAll, selectedApplicants, totalApplicants]);

  const handleModalClose = useCallback(() => {
    if (selectedApplicants.length > 0 || isSelectAll) {
      showSelectedCandidates();
    }
  }, [selectedApplicants, isSelectAll, showSelectedCandidates]);

  const handleChangeStage = useCallback(() => {
    const applicantFilter = createApplicantFilter();
    if (!jobDetails || !applicantFilter) return;

    toast.remove("toastBar");
    modalsService.openModal(ChangeStageModal, {
      pipelineId: jobDetails.pipeline.id,
      applicantFilter: applicantFilter,
      onUpdatedByFilter: () => {
        setSelectedApplicants([]);
      },
      onClose: handleModalClose,
    });
  }, [jobDetails, createApplicantFilter]);

  const handleAssignToJob = useCallback(() => {
    const applicantFilter = createApplicantFilter();
    if (!jobDetails || !applicantFilter) return;
    toast.remove("toastBar");

    modalsService.openModal(ApplicantAssignToJobModal, {
      applicantFilter: applicantFilter,
      onUpdatedByFilter: () => {
        setSelectedApplicants([]);
      },
      onClose: handleModalClose,
    });
  }, [jobDetails, createApplicantFilter]);

  const handleSendEmail = useCallback(() => {
    if ((!selectedApplicants.length && !isSelectAll) || !jobDetails) return;

    toast.remove("toastBar");

    if (selectedApplicants.length === 1 && !isSelectAll) {
      const candidate = selectedApplicants[0].candidates[0];
      modalsService.openModal(EmailSendModal, {
        singleEmail: {
          candidateId: candidate.id,
          applicantEmail: selectedApplicants[0].contacts.email || "",
        },
        onClose: handleModalClose,
      });
    } else {
      const applicantFilter = createApplicantFilter();
      if (!applicantFilter) return;

      const emailList = isSelectAll
        ? (applicants?.pages || [])
            .flatMap((page) => page?.items || [])
            .map((applicant) => applicant.contacts?.email)
            .filter((email): email is string => !!email)
        : selectedApplicants
            .map((applicant) => applicant.contacts?.email)
            .filter((email): email is string => !!email);

      modalsService.openModal(EmailSendModal, {
        massEmail: {
          applicantFilter: applicantFilter,
          applicantEmails: emailList,
          pipelineId: jobDetails.pipeline.id,
          employeeId: employeeMe?.id,
        },
        onClose: handleModalClose,
      });
    }
  }, [
    selectedApplicants,
    isSelectAll,
    applicants,
    jobDetails,
    totalApplicants,
    createApplicantFilter,
  ]);

  const { inView, ref } = useInView({
    threshold: 0.9,
    skip: isDataLoading || !hasNextPage,
    delay: 500,
  });

  useEffect(() => {
    if (isDataLoading) return;
    if (
      applicants &&
      applicants.pages.length > 0 &&
      applicants.pages[0] &&
      applicants.pages[0].metadata.total_items == 0
    )
      return;
    if (inView) {
      fetchNextPage();
    }
  }, [inView]);

  const handleChangePanelSize = (size: number) => {
    if (id) {
      setRecruitLeftPaneSize(size | 0);
    }

    analyticsService.trackEvent(analyticEvents.pageAction, {
      [analyticProperties.actionType]: "Employee App Config Changed",
      [analyticProperties.title]: "Recruiting Left Pane Size",
      [analyticProperties.id]: size,
    });
  };

  const filterEntitiesCount: number = useMemo(() => {
    let count = 0;

    if ((filter.job_ids?.length ?? 0) > 1) count++;
    if ((filter.pipeline_stage_ids?.length ?? 0) > 0 && (filter.job_ids?.length ?? 0) !== 1)
      count++;
    if ((filter.disqualification_reason_ids?.length ?? 0) > 0) count++;
    if ((filter.source_ids?.length ?? 0) > 0) count++;
    if (filter.location) count++;
    if (filter.company) count++;
    if (filter.position) count++;
    if (filter.date_last_candidate_action_from || filter.date_last_candidate_action_to) count++;
    if ((filter.label_ids?.length ?? 0) > 0) count++;
    if ((filter.recruiter_ids?.length ?? 0) > 0) count++;
    if (filter.date_applicant_birth_from || filter.date_applicant_birth_to) count++;
    if (filter.stakeholder_ids?.length ?? 0) count++;
    if (filter.action_employee_ids?.length ?? 0) count++;
    if (filter.author_ids?.length ?? 0) count++;
    if (filter.assigned_by_ids?.length ?? 0) count++;
    return count;
  }, [filter]);

  const handleNavigateToURLWithoutJobs = () => {
    searchParams.delete("job_id");
    searchParams.delete("stage_id");
    navigate({
      pathname: "/recruitment/candidates",
      search: searchParams.toString(),
    });
  };

  return (
    <Box className={"h-full flex flex-col overflow-hidden"}>
      <Stack direction={"horizontal"} className={"grow h-full overflow-hidden"}>
        <Allotment
          proportionalLayout={false}
          onDragEnd={(sizes: number[]) => {
            if (sizes.length) handleChangePanelSize(sizes[0]);
          }}
        >
          <Allotment.Pane minSize={200} preferredSize={recruitLeftPaneSize}>
            <Stack
              className={twMerge(
                "shrink-0 overflow-y-auto divide-y divide-inherit border-dark/5 bg-light/80 h-full",
                `${id ? "" : "w-full bg-light"}`
              )}
            >
              <Box>
                <Stack
                  className={"h-16 px-6"}
                  items={"center"}
                  justify={"between"}
                  direction={"horizontal"}
                >
                  <Stack
                    direction={"horizontal"}
                    items={"center"}
                    className={"overflow-hidden"}
                    gap={"xs"}
                  >
                    {sidebarIsCollapsed && !id && jobDetails && (
                      <Stack direction={"horizontal"} items={"center"} gap={"xs"}>
                        <Avatar emoji={getJobEmoji(jobDetails)} />
                        <Text
                          className={"text-title text-lg text-ellipsis overflow-hidden text-nowrap"}
                        >
                          {jobDetails.title + "  ·  "}
                        </Text>
                      </Stack>
                    )}
                    {applicants && applicants.pages.length > 0 && applicants.pages[0] ? (
                      <Text
                        className={"text-title text-lg text-ellipsis overflow-hidden text-nowrap"}
                      >
                        {t("plurals:plural_candidates", { count: totalApplicants })}
                      </Text>
                    ) : (
                      <i></i>
                    )}
                  </Stack>

                  <Stack direction={"horizontal"} gap={"sm"}>
                    <DropdownMenu
                      trigger={
                        <Button
                          variant={filterEntitiesCount == 0 ? "secondary" : "primary"}
                          leftIcon={<FontAwesomeIcon icon="fa-light fa-bars-filter" />}
                          rightIcon={
                            filterEntitiesCount > 0 ? (
                              <NotificationCount count={filterEntitiesCount} size={"sm"} />
                            ) : (
                              <></>
                            )
                          }
                        >
                          {!currentApplicantId && t("common:filter")}
                        </Button>
                      }
                    >
                      <Stack gap={"xs"}>
                        <DropdownMenuItem
                          onClick={() => {
                            setSearchParams((searchParams) => {
                              searchParams.set("sort", ApplicantSortField.last_action_at);
                              return searchParams;
                            });
                          }}
                          leftIcon={<FontAwesomeIcon icon="fa-light fa-timer" />}
                          title={t("ats:sorting_most_recent")}
                          isSelected={
                            filter.sorting.fields[0]?.field == ApplicantSortField.last_action_at
                          }
                        />
                        {isFeatureEnabled(ApiFeatureType.ats_ai) && (
                          <DropdownMenuItem
                            onClick={() => {
                              setSearchParams((searchParams) => {
                                searchParams.set("sort", ApplicantSortField.candidate_ai_score);
                                return searchParams;
                              });
                            }}
                            leftIcon={<FontAwesomeIcon icon="fa-light fa-face-smile" />}
                            title={t("ats:sorting_job_match")}
                            isSelected={
                              filter.sorting.fields[0]?.field ==
                              ApplicantSortField.candidate_ai_score
                            }
                          />
                        )}
                        <DropdownMenuItem
                          onClick={() => {
                            setSearchParams((searchParams) => {
                              searchParams.set("sort", ApplicantSortField.candidate_created_at);
                              return searchParams;
                            });
                          }}
                          leftIcon={<FontAwesomeIcon icon="fa-light fa-calendar-circle-plus" />}
                          title={t("ats:sorting_newest_candidates")}
                          isSelected={
                            filter.sorting.fields[0]?.field ==
                            ApplicantSortField.candidate_created_at
                          }
                        />
                        <DropdownMenuItem
                          onClick={handleApplicantFilterClick}
                          variant={"menu"}
                          leftIcon={<FontAwesomeIcon icon="fa-light fa-bars-filter" />}
                          title={t("common:more_filters") + "..."}
                        />
                      </Stack>
                    </DropdownMenu>
                    {canIEdit(ApiPermission.manage_candidates) && (
                      <Button
                        variant={currentApplicantId ? "secondary" : "primary"}
                        onClick={handleNewApplicantClick}
                        leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
                      >
                        {!currentApplicantId && t("ats:add_candidate")}
                      </Button>
                    )}
                  </Stack>
                </Stack>

                <Search
                  className={"w-full"}
                  innerPadding={true}
                  borderless={true}
                  placeholder={t("ats:search_placeholder_name_phone_links_email") + "..."}
                  value={searchTerm}
                  onInput={(e) => {
                    setIsUserTyping(true);
                    setSearchTerm(e.currentTarget.value);
                  }}
                />
              </Box>
              {applicants &&
                applicants.pages.map((page, pageIndex) => (
                  <Fragment key={`'applicants-page-${pageIndex}`}>
                    {page?.items?.map((applicant, index) => (
                      <Stack
                        key={`candidate-list-item-${pageIndex}-${index}`}
                        direction={"horizontal"}
                        className={
                          currentApplicantId === applicant.id
                            ? "bg-light/80"
                            : "cursor-pointer hover:bg-light/50"
                        }
                        onClick={() => {
                          handleApplicantClick(applicant);
                        }}
                      >
                        <Box
                          className={twMerge("shrink-0", id ? "w-full" : "w-1/3")}
                          ref={
                            pageIndex == applicants.pages.length - 1 &&
                            index == page?.items.length - 1
                              ? ref
                              : undefined
                          }
                        >
                          <ApplicantListItem
                            primaryText={`${applicant.first_name} ${applicant.last_name}`}
                            text2={
                              hasField(ApiFieldTypes.applicant_position)
                                ? applicant.current_position.position || "-"
                                : undefined
                            }
                            avatarUrl={applicant.photo_url}
                            text3={
                              hasField(ApiFieldTypes.applicant_company)
                                ? applicant.current_position.company || "-"
                                : undefined
                            }
                            onSelect={
                              jobDetails && canIEdit(ApiPermission.manage_candidates)
                                ? (selected: boolean) => {
                                    handleSelectApplicant(selected, applicant);
                                  }
                                : undefined
                            }
                            selected={
                              (isSelectAll &&
                                !selectedApplicants.some(
                                  (applicantIn) => applicantIn.id === applicant.id
                                )) ||
                              (!isSelectAll &&
                                selectedApplicants.some(
                                  (applicantIn) => applicantIn.id === applicant.id
                                ))
                            }
                            selectViewIsVisible={multiSelectionModeOn}
                            warning={applicant.stage_is_overdue}
                            newInformer={
                              !!applicant.last_or_unread_comment_data?.notification ||
                              applicant.have_unread_candidates
                            }
                            avatarState={
                              applicant.candidates.length > 0 &&
                              applicant.candidates[0].ai_score_status ==
                                ApiAIProcessingStatus.completed &&
                              applicant.candidates[0].ai_score
                                ? getCandidateScoreMarkAvatarState(
                                    applicant.candidates[0].ai_score.ai_score_mark
                                  )
                                : undefined
                            }
                          />
                        </Box>
                        {!id && applicant.last_action && applicant.candidates.length > 0 && (
                          <div className={"grid grid-cols-2 px-6 w-full gap-md"}>
                            <ActionItem action={applicant.last_action} dimmed />
                            <Stack items={"start"} justify={"start"} className={"w-full py-3 grow"}>
                              {applicant.candidates.slice(0, 2).map((candidate) => (
                                <Stack
                                  direction={"horizontal"}
                                  gap={"xs"}
                                  className={"w-full"}
                                  key={`${candidate.id}`}
                                >
                                  <Text>{candidate.job.title} </Text>
                                  <Text className={"text-secondary"}>{" · "}</Text>
                                  {candidate.disqualification_reason ? (
                                    <Text className={"text-secondary"}>
                                      {candidate.disqualification_reason.title}
                                    </Text>
                                  ) : (
                                    <Text className={"text-secondary"}>
                                      {candidate.pipeline_stage.title}
                                    </Text>
                                  )}
                                </Stack>
                              ))}
                            </Stack>
                          </div>
                        )}
                      </Stack>
                    ))}
                  </Fragment>
                ))}
              {isDataLoading &&
                Array.from({ length: totalApplicants === 0 ? 8 : 2 }).map((_, index) => (
                  <ApplicantListItemSkeleton key={`applicant-skeleton-${index}`} isCompact={!!id} />
                ))}
              {!isDataLoading &&
                totalApplicants == 0 &&
                filter.text &&
                filter.text.length > 0 &&
                ((filter.job_ids && filter.job_ids.length > 0) ||
                  (filter.pipeline_stage_ids && filter.pipeline_stage_ids.length > 0)) && (
                  <EmptyStateBase
                    emoji={":eyes:"}
                    title={t("ats:did_not_find_anyone")}
                    description={t("ats:try_broadening_filters")}
                    primaryButton={
                      <Button
                        onClick={() => {
                          handleNavigateToURLWithoutJobs();
                        }}
                      >
                        {t("ats:broaden_the_filter")}
                      </Button>
                    }
                  />
                )}
            </Stack>
          </Allotment.Pane>
          <Allotment.Pane visible={!!id}>
            <ApplicantDetails />
          </Allotment.Pane>
        </Allotment>
      </Stack>
    </Box>
  );
}
